import { Link2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateBr, publicationPlainText } from '@/lib/publication-inbox'
import type { InboxItem } from '@/services/publication-inbox-service'
import { cn } from '@/utils/cn'

interface InboxListProps {
  items: InboxItem[]
  loading: boolean
  selectedId?: number | null
  onSelect: (item: InboxItem) => void
  /** Marcação múltipla (caixas de seleção); ausente = lista sem seleção. */
  checked?: ReadonlySet<number>
  onToggle?: (id: number) => void
}

/** Lista compacta de publicações (clique abre o painel lateral). */
export function InboxList({ items, loading, selectedId, onSelect, checked, onToggle }: InboxListProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    )
  }
  if (items.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Nenhuma publicação com esses filtros.</p>
  }
  return (
    <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
      {items.map((item) => (
        <li key={item.id} className={cn('flex items-start', selectedId === item.id && 'bg-muted')}>
          {checked && onToggle && (
            <label className="flex shrink-0 cursor-pointer items-center self-stretch pl-3 pr-1">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={checked.has(item.id)}
                onChange={() => onToggle(item.id)}
                aria-label={`Selecionar ${item.numeroProcesso}`}
              />
            </label>
          )}
          <button
            type="button"
            onClick={() => onSelect(item)}
            className="flex min-w-0 flex-1 flex-col gap-1 px-3 py-2.5 text-left hover:bg-muted/60"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="tabular-nums">{formatDateBr(item.dataDisponibilizacao)}</span>
              <Badge variant="outline">{item.siglaTribunal}</Badge>
              <span>{item.tipoComunicacao}</span>
              {item.caso && (
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Link2 className="size-3" aria-hidden="true" /> {item.caso.numeroInterno ?? 'Processo vinculado'}
                </span>
              )}
              {item.responsavel?.nome && <span>· {item.responsavel.nome}</span>}
            </div>
            <div className="font-medium tabular-nums">{item.numeroProcesso}</div>
            <p className="line-clamp-2 text-sm text-muted-foreground">{publicationPlainText(item.resumo)}</p>
          </button>
        </li>
      ))}
    </ul>
  )
}
